import { useState, useCallback } from 'react';
import { vecToArray, parseSnapshot } from '../wasmBridge';

const MAX_STEPS = 10000;

// Encapsulates all solver state: query execution, stepping, and snapshot management.
// Returns derived values (display, isAtLatest) as well as the raw setter for displayIdx
// so the scrubber in App.js can seek without going through an action creator.
export function useSolver(moduleRef) {
    const [snapshots,   setSnapshots]   = useState([]);
    const [displayIdx,  setDisplayIdx]  = useState(0);
    const [queryKey,    setQueryKey]    = useState(0);
    const [clauseError, setClauseError] = useState('');
    const [status,      setStatus]      = useState('');
    const [idVec,       setIdVec]       = useState(null);
    const [varIdVec,    setVarIdVec]    = useState(null);
    const [namedVars,   setNamedVars]   = useState([]);
    const [clauseRanges, setClauseRanges] = useState([]);

    // Calls load_and_query on the WASM module and refreshes identifier/range tables.
    // Returns true on success, false if the module reported a parse/load error.
    const loadQuery = useCallback((clauses, query, tailCallOpt) => {
        const m = moduleRef.current;
        const err = m.load_and_query(clauses, query, tailCallOpt);
        if (err) { setClauseError(err); return false; }
        setClauseError('');
        setIdVec(vecToArray(m.get_names()));
        setVarIdVec(vecToArray(m.get_var_names()));
        setNamedVars(vecToArray(m.get_named_variables()).map(nv => ({
            name: nv.name,
            termIdx: nv.term_idx,
        })));
        setClauseRanges(vecToArray(m.get_clause_ranges()).map(r => ({
            startLine: r.start_line + 1,
            startCol:  r.start_col  + 1,
            endLine:   r.end_line   + 1,
            endCol:    r.end_col    + 1,
        })));
        return true;
    }, [moduleRef]);

    // Run the query and capture only the initial snapshot.
    const runQuery = useCallback((clauses, query, tailCallOpt) => {
        try {
            if (!loadQuery(clauses, query, tailCallOpt)) return;
            const snap = parseSnapshot(moduleRef.current.get_state_snapshot());
            setSnapshots([snap]);
            setDisplayIdx(0);
            setQueryKey(k => k + 1);
            setStatus('');
        } catch (e) {
            setStatus(`Error: ${e}`);
        }
    }, [loadQuery, moduleRef]);

    // Run the query to completion, collecting all snapshots (up to MAX_STEPS).
    const runAll = useCallback((clauses, query, tailCallOpt) => {
        try {
            if (!loadQuery(clauses, query, tailCallOpt)) return;
            const snaps = [parseSnapshot(moduleRef.current.get_state_snapshot())];
            while (!snaps[snaps.length - 1].atEnd && snaps.length < MAX_STEPS) {
                moduleRef.current.step();
                snaps.push(parseSnapshot(moduleRef.current.get_state_snapshot()));
            }
            setSnapshots(snaps);
            setDisplayIdx(snaps.length - 1);
            setQueryKey(k => k + 1);
            setStatus(snaps.length >= MAX_STEPS ? `Step limit (${MAX_STEPS}) reached.` : '');
        } catch (e) {
            setStatus(`Error: ${e}`);
        }
    }, [loadQuery, moduleRef]);

    // Advance by one step and append the resulting snapshot.
    const step = useCallback(() => {
        try {
            moduleRef.current.step();
            const snap = parseSnapshot(moduleRef.current.get_state_snapshot());
            setSnapshots(prev => [...prev, snap]);
            setDisplayIdx(prev => prev + 1);
        } catch (e) {
            setStatus(`Error: ${e}`);
        }
    }, [moduleRef]);

    const display    = snapshots[displayIdx] ?? null;
    const isAtLatest = displayIdx === snapshots.length - 1;

    return {
        snapshots, displayIdx, setDisplayIdx,
        queryKey, clauseError, status,
        idVec, varIdVec, namedVars, clauseRanges,
        display, isAtLatest,
        runQuery, runAll, step,
    };
}
